import { usePageFilterSaver } from 'hooks/usePageFilterSaver';
import type { DatePickerPropOnChange, DatePickerPropOnError, FilterComponentProps } from 'uikit';
import { DatePicker, TableFilterContainer, Tooltip } from 'uikit';

import type { IAfterGuiAttachedParams } from 'ag-grid-community';
import { useGridFilter, type CustomFilterProps } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState } from 'react';

const formatDate = (date: Date) => date.toLocaleDateString();

export const DateRangeFilter = (
    props: FilterComponentProps & CustomFilterProps & { changeFilterHandler: (values: any) => void },
) => {
    const hidePopupRef = useRef<() => void | null>();

    const { filterValue, onConfirm, onCancel, fieldName, colDef, context, changeFilterHandler, onModelChange } = props;

    const doesFilterPass = useCallback(() => true, []);
    const afterGuiAttached = useCallback(({ hidePopup }: IAfterGuiAttachedParams) => {
        hidePopupRef.current = hidePopup;
    }, []);

    useGridFilter({
        doesFilterPass,
        afterGuiAttached,
    });

    const ref = useRef<HTMLDivElement>(null);

    const { savedFilterState, handleSaveFilterState } = usePageFilterSaver({});

    const { field: agFieldName } = colDef ?? {};

    const actualFilterSavedValues = useMemo(() => {
        if (filterValue) {
            return filterValue as [Date?, Date?];
        }

        const name = fieldName ?? agFieldName;

        if (context?.tableUniqueId) {
            return name
                ? Object.values(savedFilterState?.[context?.tableUniqueId]?.[(name as string) ?? ''] ?? {}).map((d) =>
                      d ? new Date(d as string) : undefined,
                  )
                : [undefined, undefined];
        }

        return name
            ? Object.values(savedFilterState?.[(name as string) ?? ''] ?? {}).map((d) => new Date(d as string))
            : [undefined, undefined];
    }, [agFieldName, context?.tableUniqueId, fieldName, filterValue, savedFilterState]);

    const [value, setValue] = useState<[Date?, Date?] | null>(actualFilterSavedValues as [Date?, Date?]);
    const [error, setError] = useState<string | undefined>();

    const onChange: DatePickerPropOnChange<'date-range'> = ({ value: newValue }) => {
        setValue(newValue);
        setError(undefined);
    };

    const onError: DatePickerPropOnError = (propsDate) => {
        if (propsDate.type === 'invalidInputAttempt') {
            setError(`Даты ${propsDate.stringValue} не существует`);
        }
        if (propsDate.type === 'startDateIsGreaterThanEndDate') {
            setError(
                `Дата начала (${formatDate(propsDate.date[0])}) больше чем дата конца (${formatDate(
                    propsDate.date[1],
                )})`,
            );
        }
    };

    const handleConfirm = useCallback(() => {
        const name = fieldName ?? agFieldName;

        if (name) {
            handleSaveFilterState({
                key: name as string,
                newState: value?.map((d) => (d ? d?.toISOString() : undefined)),
                rootKey: context?.tableUniqueId,
            });
        }
        onConfirm?.(value);
        changeFilterHandler?.({ fieldName: name, checkedValues: value });
        onModelChange?.({ fieldName: name, checkedValues: value });
        hidePopupRef?.current?.();
    }, [
        agFieldName,
        changeFilterHandler,
        context?.tableUniqueId,
        fieldName,
        handleSaveFilterState,
        onConfirm,
        onModelChange,
        value,
    ]);

    const handleCancel = useCallback(() => {
        hidePopupRef?.current?.();
        onCancel?.();
    }, [onCancel]);

    return (
        <TableFilterContainer onConfirm={handleConfirm} onCancel={handleCancel}>
            <DatePicker
                ref={ref}
                status={error ? 'alert' : undefined}
                value={value}
                onChange={onChange}
                type="date-range"
                onError={onError}
                withClearButton
                dropdownClassName="ag-custom-component-popup"
            />
            {error && (
                <Tooltip status="alert" anchorRef={ref}>
                    {error}
                </Tooltip>
            )}
        </TableFilterContainer>
    );
};

DateRangeFilter.displayName = 'DateRangeFilter';
